import {
  ICellRendererParams,
  RowDragEndEvent,
  RowDragEnterEvent,
  ValueFormatterParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { RefObject, useCallback, useRef } from "react";
import { HiOutlineTrash } from "react-icons/hi";

import { OrderDetailFormRowValues } from "../../model/form/OrderDetailForm";
import { QuotationDetailFormRowValues } from "../../model/form/QuotationDetailForm";
import { agNumberFormatter } from "../../util/AgGridUtil";
import { ProductCodeConst } from "../../util/Constant";
import { calculateTax } from "../../util/TaxUtil";

/**
 * 注文詳細（OrderDetail）と見積詳細（QuotationDetail）で共通の明細グリッド処理
 */

/** 翻訳関数（useTranslation の t）を受け取るための型 */
export type TranslateFn = (key: string) => string;

/** 注文・見積の明細行が共通で持つ最小限の項目 */
type DetailRowBase = {
  key: number;
  productCode: string;
  replyDetailSubTotal?: number;
};

// ---------------------------------------------------------------------------
// 空行判定
// ---------------------------------------------------------------------------

/**
 * 空行判定
 * 指定した項目がすべて未入力（falsy）の場合に空行とみなす。
 */
function isEmptyDetailRow<T>(row: T, fields: readonly (keyof T)[]): boolean {
  return fields.every((field) => !row[field]);
}

/** 注文明細の空行判定（品目コード・型式・数量・単位・回答単価・回答数量がすべて空） */
export function isEmptyOrderRow(row: OrderDetailFormRowValues) {
  return isEmptyDetailRow(row, [
    "productCode",
    "spec",
    "quantity",
    "unit",
    "replyUnitCost",
    "replyQuantity",
  ]);
}

/** 見積明細の空行判定（品目コード・型式・数量・回答単価・回答数量がすべて空） */
export function isEmptyQuotationRow(row: QuotationDetailFormRowValues) {
  return isEmptyDetailRow(row, [
    "productCode",
    "spec",
    "quantity",
    "replyUnitCost",
    "replyQuantity",
  ]);
}

// ---------------------------------------------------------------------------
// 金額計算・行操作
// ---------------------------------------------------------------------------

/**
 * 回答金額（小計・税額・合計）を計算する
 * 合計 = (回答明細小計の合計 - 割引額) + 税額
 */
export function calculateReplyPriceCore(
  gridRows: DetailRowBase[],
  replyDiscountPrice: number | undefined,
  replyTax: number | undefined
) {
  let subtotalPrice = 0;
  gridRows.forEach((row) => {
    subtotalPrice += row.replyDetailSubTotal ?? 0;
  });
  const subtotalMinusDiscount = subtotalPrice - (replyDiscountPrice ?? 0);
  const taxPrice = calculateTax(subtotalMinusDiscount, replyTax);
  const totalPrice = subtotalMinusDiscount + taxPrice;

  return { subtotalPrice, taxPrice, totalPrice };
}

/** 経費行追加可否判定（経費行がまだ存在しなければ追加可） */
export function canAddExpensesRow(rows: DetailRowBase[]) {
  return (
    rows.filter((it) => it.productCode === ProductCodeConst.EXPENSES).length ===
    0
  );
}

// ---------------------------------------------------------------------------
// 列定義の共通部品
// ---------------------------------------------------------------------------

/** 品目マスタに存在しない行を赤枠にする（b-error-cell） */
export const errorCellRule = (params: any) => !params.data?.existFlg;

/**
 * 編集可能セルの背景色（b-editable-cell）
 * 編集可否判定関数と品目マスタ存在フラグの両方を満たす場合に適用する
 */
export function editableCellRule<T>(
  editable: (params: ICellRendererParams<T>) => boolean
) {
  return (params: any) => editable(params) && params.data?.existFlg;
}

/**
 * 空行・「---」行では空文字を返し、それ以外は数値フォーマットする valueFormatter を生成する
 * @param isEmptyRow 画面ごとの空行判定
 * @param emptyValueText 値が未設定のときに表示する文字（例: 要問合せ）
 */
export function createDetailNumberFormatter(
  isEmptyRow: (row: any) => boolean,
  emptyValueText?: string
) {
  return (e: ValueFormatterParams) => {
    if (isEmptyRow(e.data) || e.data.productCode === ProductCodeConst.BLANK) {
      return "";
    }
    return agNumberFormatter(e, emptyValueText);
  };
}

/**
 * 行削除ボタン（cellRenderer 用）
 * @param rowData ag-Grid のセル描画パラメータ
 * @param canDelete 削除可否（false のときは何も描画しない）
 * @param onClick 削除ボタン押下時（引数は行の key）
 */
export function renderDetailDeleteButton<T extends { key: number }>(
  rowData: ICellRendererParams<T>,
  canDelete: boolean,
  onClick: (key: number) => void
) {
  if (!canDelete) {
    return <></>;
  }
  return (
    <span>
      <a
        style={{ color: "black" }}
        href="#"
        onClick={(e) => onClick(rowData.data?.key ?? -1)}
      >
        <div style={{ display: "flex", justifyContent: "center" }}>
          <HiOutlineTrash size={20} style={{ margin: "10 10px" }} />
        </div>
      </a>
    </span>
  );
}

// ---------------------------------------------------------------------------
// 行ドラッグ
// ---------------------------------------------------------------------------

type DetailRowDragParams<T> = {
  /** 明細グリッドの参照 */
  gridRef: RefObject<AgGridReact>;
  /** 明細番号のプロパティ名（orderDetailNo / quotationDetailNo） */
  detailNoKey: keyof T;
  /** 行追加可否 */
  canAddNewRow: () => boolean;
  /** 新規行追加（rows を直接更新する） */
  addNewRow: (rows: T[]) => void;
  /** 並び替え後の明細を state に反映する */
  setRows: (rows: T[]) => void;
  /** ユーザ入力有無フラグを立てる */
  setFormChanged: (changed: boolean) => void;
};

/**
 * 明細グリッドの行ドラッグ（並び替え）ハンドラ
 * 並び替え後に明細番号を振り直し、最下行を移動させた場合は新規入力行を追加する。
 *
 * 依存配列を空にしているため、ハンドラ内で参照する canAddNewRow / addNewRow は
 * 初回レンダリング時に渡されたものが使われ続ける。
 */
export function useDetailRowDrag<T>(params: DetailRowDragParams<T>) {
  const {
    gridRef,
    detailNoKey,
    canAddNewRow,
    addNewRow,
    setRows,
    setFormChanged,
  } = params;
  // ドラッグ開始時の行インデックス
  const dragRowIndex = useRef(-1);

  const onRowDragEnter = useCallback((e: RowDragEnterEvent) => {
    dragRowIndex.current = e.overIndex;
  }, []);

  const onRowDragEnd = useCallback((e: RowDragEndEvent) => {
    setFormChanged(true);
    // 並び替え後の順番でNoをつけなおす
    const newDatas: T[] = [];
    gridRef.current?.api.forEachNode((node, index) => {
      const row = node.data as T;
      (row as Record<keyof T, unknown>)[detailNoKey] = index + 1;
      newDatas.push(row);
    });
    // 最下行を移動させた場合は新規行を追加
    if (
      dragRowIndex.current !== e.overIndex &&
      dragRowIndex.current === newDatas.length - 1 &&
      canAddNewRow()
    ) {
      addNewRow(newDatas);
    }

    setRows(newDatas);
  }, []);

  return { onRowDragEnter, onRowDragEnd };
}

// ---------------------------------------------------------------------------
// クリップボード
// ---------------------------------------------------------------------------

/**
 * 明細の内容をタブ区切りテキストにしてクリップボードへコピーする
 * 出力する値の文字列化（未設定時の扱いを含む）は呼び出し側で行う。
 * @param rows 明細行
 * @param headers 1行目の見出し
 * @param toValues 行から出力する値の配列
 */
export async function copyDetailsToClipboard<T>(
  rows: T[],
  headers: string[],
  toValues: (row: T) => unknown[]
) {
  let copyText = headers.join("\t") + "\n";
  rows.forEach((row) => {
    toValues(row).forEach((value) => {
      copyText += value + "\t";
    });
    copyText += "\n";
  });
  await global.navigator.clipboard.writeText(copyText);
}
